import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Star, FileText, Image as ImageIcon, CheckCircle2 } from "lucide-react";
import { PublicHeader, Footer } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { useI18n } from "../lib/i18n";
import { api, euro, fileUrl, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const TIMELINE = [
  { key: "received", statuses: ["awaiting_payment", "searching_partner", "partner_assigned", "appointment_pending", "appointment_confirmed", "in_progress", "completed"], label_nl: "Aanvraag ontvangen", label_fr: "Demande reçue" },
  { key: "searching", statuses: ["searching_partner", "partner_assigned", "appointment_pending", "appointment_confirmed", "in_progress", "completed"], label_nl: "Hersteller zoeken", label_fr: "Recherche d'artisan" },
  { key: "assigned", statuses: ["partner_assigned", "appointment_pending", "appointment_confirmed", "in_progress", "completed"], label_nl: "Hersteller toegewezen", label_fr: "Artisan assigné" },
  { key: "appointment", statuses: ["appointment_confirmed", "in_progress", "completed"], label_nl: "Afspraak bevestigd", label_fr: "Rendez-vous confirmé" },
  { key: "executed", statuses: ["in_progress", "completed"], label_nl: "Herstelling uitgevoerd", label_fr: "Réparation effectuée" },
  { key: "done", statuses: ["completed"], label_nl: "Opdracht afgerond", label_fr: "Commande terminée" },
];

function Timeline({ status, lang }) {
  return (
    <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
      {TIMELINE.map((s) => {
        const done = s.statuses.includes(status);
        return (
          <li key={s.key} className="ml-4">
            <span className={`absolute -left-[9px] h-4 w-4 rounded-full border-2 ${done ? "bg-green-500 border-green-500" : "bg-white border-slate-300"}`} />
            <span className={`text-sm ${done ? "font-semibold text-slate-900" : "text-slate-400"}`}>{lang === "fr" ? s.label_fr : s.label_nl}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function Account() {
  const { t, lang } = useI18n();
  const [orders, setOrders] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = () => api.get("/bookings/mine").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/bookings/${id}`);
    setSelected(data);
    setRating(5); setComment("");
  };

  const submitReview = async () => {
    try {
      await api.post(`/bookings/${selected.id}/review`, { rating, comment });
      toast.success("Bedankt voor uw beoordeling!");
      const { data } = await api.get(`/bookings/${selected.id}`);
      setSelected(data);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">{t("account.title")}</h1>
          <Link to="/boeken"><Button className="cta-orange rounded-full" data-testid="account-book-btn">{t("account.book")}</Button></Link>
        </div>

        {orders === null ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : orders.length === 0 ? (
          <div className="mt-10 text-center bg-white border border-slate-200 rounded-xl p-12" data-testid="account-empty">
            <p className="text-slate-500">{t("account.empty")}</p>
            <Link to="/boeken"><Button className="cta-orange rounded-full mt-6">{t("account.book")}</Button></Link>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {orders.map((o) => (
              <button key={o.id} onClick={() => openDetail(o.id)} data-testid={`account-order-${o.booking_number}`}
                className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-all flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold text-slate-900">{o.booking_number}</span>
                    <StatusBadge status={o.status} />
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{o.service_name} · {o.quantity}x · {o.location.city}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={o.payment_status} />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
      <Footer />

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="account-order-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">{selected.booking_number} <StatusBadge status={selected.status} /></DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">{t("account.timeline")}</h4>
                  <Timeline status={selected.status} lang={lang} />
                </div>
                <div className="space-y-2 text-sm">
                  <Info label={t("wizard.summary.repair")} value={selected.service_name} />
                  <Info label={t("wizard.summary.material")} value={`${selected.material} · ${selected.item_type}`} />
                  <Info label={t("wizard.summary.qty")} value={selected.quantity} />
                  <Info label={t("wizard.summary.location")} value={`${selected.location.street} ${selected.location.house_number}, ${selected.location.postal_code} ${selected.location.city}`} />
                  <Info label={t("wizard.summary.appointment")} value={t(`wizard.${selected.appointment_preference}`)} />
                  <Info label="Hersteller" value={selected.partner_name || "—"} />
                </div>
              </div>

              {selected.photos?.length > 0 && (
                <div className="mt-2">
                  <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-1"><ImageIcon size={16} /> Foto's</h4>
                  <div className="flex gap-2 flex-wrap">
                    {selected.photos.map((p) => <img key={p.id} src={fileUrl(p.path)} alt="" className="h-20 w-20 object-cover rounded-lg border" />)}
                  </div>
                </div>
              )}

              {selected.invoice && (
                <div className="rounded-lg border border-slate-200 p-4 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><FileText size={18} className="text-slate-500" /><span className="font-semibold text-slate-900">{selected.invoice.invoice_number}</span></div>
                    <span className="font-bold">{euro(selected.invoice.total)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Btw {selected.invoice.vat_rate}% · {euro(selected.invoice.vat_amount)}</p>
                </div>
              )}

              {selected.status === "completed" && (
                selected.review ? (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex items-center gap-2 text-green-800">
                    <CheckCircle2 size={18} /> <span className="text-sm">Bedankt voor uw beoordeling ({selected.review.rating}/5)</span>
                  </div>
                ) : (
                  <div className="rounded-lg border border-slate-200 p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">{t("account.leavereview")}</h4>
                    <div className="flex gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <button key={i} onClick={() => setRating(i)} data-testid={`review-star-${i}`}>
                          <Star size={26} className={i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                        </button>
                      ))}
                    </div>
                    <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Hoe tevreden bent u?" data-testid="review-comment" />
                    <Button className="cta-orange rounded-full mt-3" onClick={submitReview} data-testid="review-submit">{t("account.leavereview")}</Button>
                  </div>
                )
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right">{value}</span>
    </div>
  );
}
